import { useCallback, useEffect, useRef, useState } from 'react'

// Notices when a newer build has been deployed while this tab was open.
//
// A deploy replaces index.html and the hashed bundle, but an already-loaded tab
// keeps running whatever it downloaded — indefinitely, and with no sign that it
// is behind. The only cure is a reload, so the job here is to find out and say
// so; the reload itself stays the user's decision (see UpdateBanner).
//
// The version is CRA's own content hash. Every build writes asset-manifest.json
// with the current main.js filename, so there is nothing to add to the build and
// no version constant to remember to bump:
//
//     { "files": { "main.js": "/static/js/main.5b6e6768.js", ... } }
//
// and the running tab reads its OWN filename off the script tag the build put in
// its HTML. Comparing those two is the whole mechanism.

const MANIFEST = '/asset-manifest.json'
const POLL_MS = 5 * 60 * 1000

/** The main.js path this tab actually loaded, or null under the dev server. */
export const runningBundle = (doc = document) => {
  const el = doc.querySelector('script[src*="/static/js/main."]')
  if (!el) return null
  try {
    return new URL(el.src, window.location.origin).pathname
  } catch (e) {
    return null
  }
}

/** The main.js path a fetched asset-manifest advertises, or null if unusable. */
export const bundleFromManifest = manifest => {
  const file = manifest && manifest.files && manifest.files['main.js']
  return typeof file === 'string' && file ? file : null
}

/**
 * A difference only counts when BOTH sides are known. A missing running bundle
 * means the dev server, and a missing deployed one means the manifest could not
 * be read — neither is evidence of a new version, and announcing one on that
 * basis would put an un-dismissable banner in front of someone whose network
 * merely hiccuped.
 */
export const isUpdate = (running, deployed) =>
  Boolean(running && deployed && running !== deployed)

export const useAppVersion = ({ pollMs = POLL_MS } = {}) => {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const running = useRef(null)
  if (running.current === null) running.current = runningBundle()

  useEffect(() => {
    // No hashed bundle: the dev server serves bundle.js unhashed, and there is
    // nothing meaningful to compare. Never poll there.
    if (!running.current) return

    let done = false

    const check = async () => {
      if (done) return
      try {
        // no-store AND a cache-buster. asset-manifest.json is served with an
        // etag and last-modified but no cache-control, which lets the browser
        // cache it heuristically — without this we would faithfully compare one
        // stale copy against another and never notice a thing.
        const res = await fetch(`${MANIFEST}?t=${Date.now()}`, { cache: 'no-store' })
        if (!res.ok) return
        const deployed = bundleFromManifest(await res.json())
        if (done) return
        if (isUpdate(running.current, deployed)) {
          setUpdateAvailable(true)
          done = true // one answer is enough — stop polling
        }
      } catch (e) {
        // Offline, or a deploy mid-flight briefly serving nothing. Stay quiet
        // and try again on the next tick.
      }
    }

    // Returning to a tab left open is the common case, and far more likely to
    // matter than any particular interval — check on both.
    const onVisible = () => { if (document.visibilityState === 'visible') check() }

    const timer = setInterval(check, pollMs)
    document.addEventListener('visibilitychange', onVisible)
    check()

    return () => {
      done = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [pollMs])

  const reload = useCallback(() => window.location.reload(), [])

  return { updateAvailable, reload }
}
